// src/VendorsSettings.js
import React, { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
import AddressAutocomplete from './AddressAutocomplete';
import TitleBar from './components/ui/TitleBar';

const API_BASE = getApiBase();

const VendorsSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [vendors, setVendors] = useState([]);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [vendorForm, setVendorForm] = useState({
    name: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    notes: ''
  });

  const formatPhone = (value) => {
    const cleaned = String(value || '').replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVendors = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/settings/vendors`, {
        headers: { ...getTenantHeaders() }
      });
      const data = await res.json().catch(() => null);
      setVendors(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load vendors:', e);
      setVendors([]);
      alert('Error loading vendors from database.');
    }
  };

  const resetForm = () => {
    setVendorForm({
      name: '',
      contactName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      notes: ''
    });
  };

  const addVendor = async () => {
    if (!vendorForm.name.trim()) return alert('Enter vendor name');

    try {
      const res = await fetch(`${API_BASE}/api/settings/vendors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(vendorForm)
      });
      if (!res.ok) throw new Error('Save failed');

      setShowAddVendor(false);
      resetForm();
      loadVendors();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'VENDOR CREATED!' }
      }));
    } catch (e) {
      console.error('Add vendor failed:', e);
      alert('Failed to save vendor.');
    }
  };

  const saveEditVendor = async () => {
    if (!vendorForm.name.trim()) return alert('Enter vendor name');

    try {
      const res = await fetch(`${API_BASE}/api/settings/vendors/${editingVendor}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(vendorForm)
      });
      if (!res.ok) throw new Error('Update failed');

      setEditingVendor(null);
      resetForm();
      loadVendors();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'VENDOR UPDATED!' }
      }));
    } catch (e) {
      console.error('Edit vendor failed:', e);
      alert('Failed to update vendor.');
    }
  };

  const deleteVendor = async (id) => {
    if (!window.confirm('DELETE THIS VENDOR?')) return;

    try {
      const res = await fetch(`${API_BASE}/api/settings/vendors/${id}`, {
        method: 'DELETE',
        headers: { ...getTenantHeaders() }
      });
      if (!res.ok) throw new Error('Delete failed');
      loadVendors();
    } catch (e) {
      console.error('Delete vendor failed:', e);
      alert('Failed to delete vendor.');
    }
  };

  const styles = {
    container: {
      padding: isPhone ? '15px 4px' : (isMobile ? '15px' : '40px'),
      backgroundColor: '#E8E2DA',
      minHeight: '100vh'
    },
    headerWrap: {
      maxWidth: '1100px',
      margin: '0 auto 22px auto'
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 18px',
      backgroundColor: '#000',
      color: '#FFA500',
      border: '4px solid #E8761D',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    panel: {
      maxWidth: '1100px',
      margin: '0 auto',
      backgroundColor: '#E7B888',
      border: '8px solid black',
      padding: isPhone ? '15px 5px' : (isMobile ? '15px' : '28px')
    },

    // TITLE BAR
    titleBar: {
      backgroundColor: 'white',
      color: 'black',
      border: '6px solid black',
      padding: isMobile ? '14px 14px' : '16px 18px',
      fontWeight: 900,
      textTransform: 'uppercase',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '10px',
      marginBottom: '16px'
    },
    titleBarInner: {
      border: '2px solid #E8761D',
      padding: isMobile ? '10px 10px' : '10px 12px',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      boxSizing: 'border-box'
    },
    titleText: {
      fontSize: isMobile ? '18px' : '22px',
      letterSpacing: '0.5px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },

    // ACTION ROW: Add + Search side-by-side
    actionRow: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      marginBottom: '16px',
      flexWrap: 'wrap'
    },
    addBtn: {
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px 18px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap'
    },
    searchInput: {
      flex: 1,
      minWidth: isMobile ? '100%' : '320px',
      padding: '12px 14px',
      border: '4px solid black',
      fontWeight: 900,
      fontSize: '16px',
      boxSizing: 'border-box',
      textTransform: 'uppercase'
    },

    card: {
      border: '4px solid black',
      padding: '14px',
      marginBottom: '12px',
      backgroundColor: 'white'
    },
    cardTitle: { fontWeight: 900, fontSize: '18px' },
    meta: { fontWeight: 900, marginTop: '4px', overflowWrap: isPhone ? 'anywhere' : undefined },
    cardActions: { marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' },
    btn: {
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      background: '#FFD400',
      color: 'black'
    },
    btnCall: {
      background: '#008000',
      color: '#FFFFFF'
    },
    btnEmail: {
      background: '#0000FF',
      color: '#FFFFFF'
    },
    btnDanger: {
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      background: '#ff4444',
      color: 'white'
    },

    modal: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '18px',
      zIndex: 5000
    },
    modalContent: {
      background: 'white',
      border: '8px solid black',
      padding: '20px',
      width: '100%',
      maxWidth: '650px',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    modalTitle: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '20px',
      marginBottom: '14px'
    },
    input: {
      width: '100%',
      padding: '12px',
      marginBottom: '10px',
      border: '3px solid black',
      fontWeight: 900,
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      marginBottom: '10px',
      border: '3px solid black',
      fontWeight: 900,
      boxSizing: 'border-box',
      minHeight: '110px',
      fontFamily: 'inherit'
    },
    btnRow: { display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' },
    saveBtn: {
      flex: 1,
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      minWidth: '160px'
    },
    cancelBtn: {
      flex: 1,
      background: 'white',
      color: 'black',
      border: '4px solid black',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      minWidth: '160px'
    }
  };

  const q = String(searchTerm || '').trim().toLowerCase();
  const filteredVendors = !q
    ? vendors
    : vendors.filter((v) => {
        const hay = [
          v?.name,
          v?.contactName,
          v?.phone,
          v?.email,
          v?.address,
          v?.city,
          v?.state,
          v?.zip,
          v?.notes
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });

  const closeModal = () => {
    setShowAddVendor(false);
    setEditingVendor(null);
    resetForm();
  };

  return (
    <div style={styles.container}>
      <style>{`
        .jfn-hover { transition: all 0.12s ease; }
        .jfn-hover:active { transform: translateY(0px); }

        /* BLUE hover (Back/Add/Save/Cancel/etc) */
        .jfn-hover-blue:hover {
          background-color: #00BFFF !important;
          color: #000 !important;
          border-color: #000 !important;
          transform: translateY(-2px);
          box-shadow: 0 0 0 4px #00BFFF inset;
          filter: brightness(1.03);
        }

        /* GOLD hover (Vendor card actions) */
        .jfn-vendor-action:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #000 !important;
          transform: translateY(-2px);
          filter: brightness(1.03);
        }

        @media (max-width: 1024px) {
          .jfn-hover-blue:hover,
          .jfn-vendor-action:hover { transform: none !important; }
        }
      `}</style>

      <div style={styles.headerWrap}>
        <button
          className="jfn-hover jfn-hover-blue"
          style={styles.backButton}
          onClick={() => navigate('/settings')}
        >
          <ArrowLeft size={18} /> BACK TO SETTINGS
        </button>
      </div>

      <TitleBar
        icon="🏪"
        title="VENDORS / SUPPLIERS"
        accent="#E8761D"
        wrapStyle={{ maxWidth: '1100px', margin: '0 auto 16px auto' }}
      />

      <div style={styles.panel}>
        {/* ADD + SEARCH ROW */}
        <div style={styles.actionRow}>
          <button
            className="jfn-hover jfn-hover-blue"
            style={styles.addBtn}
            onClick={() => setShowAddVendor(true)}
          >
            + ADD VENDOR
          </button>

          <input
            style={styles.searchInput}
            placeholder="SEARCH (NAME / CONTACT / PHONE / EMAIL)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {filteredVendors.length === 0 ? (
          <div style={{ ...styles.card, backgroundColor: '#ffffcc' }}>
            <div style={{ fontWeight: 900 }}>{q ? 'No matches found.' : 'No vendors added yet.'}</div>
          </div>
        ) : (
          filteredVendors.map((v) => {
            const storedPhone = String(v?.phone ?? '').trim();
            const normalizedPhone = storedPhone.startsWith('+')
              ? `+${storedPhone.slice(1).replace(/\D/g, '')}`
              : storedPhone.replace(/\D/g, '');
            const phoneDigitCount = normalizedPhone.replace(/\D/g, '').length;
            const callHref = phoneDigitCount >= 7 && phoneDigitCount <= 15
              ? `tel:${normalizedPhone}`
              : '';
            const storedEmail = String(v?.email ?? '').trim();
            const emailHref = storedEmail ? `mailto:${storedEmail}` : '';

            return (
              <div key={v.id} style={styles.card}>
                <div style={styles.cardTitle}>{v.name}</div>
                {v.contactName && <div style={styles.meta}>Contact: {v.contactName}</div>}
                {v.phone && <div style={styles.meta}>Phone: {v.phone}</div>}
                {v.email && <div style={styles.meta}>Email: {v.email}</div>}
                {(v.address || v.city || v.state || v.zip) && (
                  <div style={styles.meta}>
                    Address: {[v.address, v.city, v.state, v.zip].filter(Boolean).join(', ')}
                  </div>
                )}
                {v.notes && <div style={{ ...styles.meta, fontStyle: 'italic' }}>{v.notes}</div>}

                <div style={styles.cardActions}>
                  {callHref ? (
                    <button
                      type="button"
                      className="jfn-hover jfn-vendor-action"
                      style={{ ...styles.btn, ...styles.btnCall }}
                      onClick={() => { window.location.href = callHref; }}
                    >
                      CALL
                    </button>
                  ) : null}

                  {emailHref ? (
                    <button
                      type="button"
                      className="jfn-hover jfn-vendor-action"
                      style={{ ...styles.btn, ...styles.btnEmail }}
                      onClick={() => { window.location.href = emailHref; }}
                    >
                      EMAIL
                    </button>
                  ) : null}

                  <button
                    className="jfn-hover jfn-vendor-action"
                    style={styles.btn}
                    onClick={() => {
                      setEditingVendor(v.id);
                      setVendorForm({
                        name: v.name || '',
                        contactName: v.contactName || '',
                        email: v.email || '',
                        phone: v.phone || '',
                        address: v.address || '',
                        city: v.city || '',
                        state: v.state || '',
                        zip: v.zip || '',
                        notes: v.notes || ''
                      });
                    }}
                  >
                    EDIT
                  </button>

                  <button
                    className="jfn-hover jfn-vendor-action"
                    style={styles.btnDanger}
                    onClick={() => deleteVendor(v.id)}
                  >
                    DELETE
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {(showAddVendor || editingVendor) && (
        <div style={styles.modal} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalTitle}>{editingVendor ? 'EDIT VENDOR' : 'ADD VENDOR'}</div>

            <input
              style={styles.input}
              placeholder="VENDOR NAME *"
              value={vendorForm.name}
              onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
            />

            <input
              style={styles.input}
              placeholder="CONTACT NAME"
              value={vendorForm.contactName}
              onChange={(e) => setVendorForm({ ...vendorForm, contactName: e.target.value })}
            />

            <input
              style={styles.input}
              placeholder="EMAIL"
              value={vendorForm.email}
              onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
            />

            <input
              style={styles.input}
              placeholder="PHONE"
              value={vendorForm.phone}
              onChange={(e) => setVendorForm({ ...vendorForm, phone: formatPhone(e.target.value) })}
            />

            <AddressAutocomplete
              inputStyle={styles.input}
              placeholder="ADDRESS"
              value={vendorForm.address}
              onChange={(address) => setVendorForm((current) => ({ ...current, address }))}
              onAddressSelect={({ street, city, state, zip }) => {
                setVendorForm((current) => ({
                  ...current,
                  address: street,
                  city,
                  state: state.slice(0, 2).toUpperCase(),
                  zip
                }));
              }}
            />

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '10px' }}>
              <input
                style={styles.input}
                placeholder="CITY"
                value={vendorForm.city}
                onChange={(e) => setVendorForm({ ...vendorForm, city: e.target.value })}
              />
              <input
                style={styles.input}
                placeholder="STATE"
                value={vendorForm.state}
                onChange={(e) => setVendorForm({ ...vendorForm, state: e.target.value })}
              />
              <input
                style={styles.input}
                placeholder="ZIP"
                value={vendorForm.zip}
                onChange={(e) => setVendorForm({ ...vendorForm, zip: e.target.value })}
              />
            </div>

            <textarea
              style={styles.textarea}
              placeholder="NOTES"
              value={vendorForm.notes}
              onChange={(e) => setVendorForm({ ...vendorForm, notes: e.target.value })}
            />

            <div style={styles.btnRow}>
              <button
                className="jfn-hover jfn-hover-blue"
                style={styles.saveBtn}
                onClick={editingVendor ? saveEditVendor : addVendor}
              >
                SAVE
              </button>
              <button className="jfn-hover jfn-hover-blue" style={styles.cancelBtn} onClick={closeModal}>
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorsSettings;
